// File: frontend/src/components/reels/CreateReelModal.jsx
import React, { useState, useRef } from 'react';
import './CreateReelModal.css';

// ✅ FIXED: Use correct API base URL without removing /api/v1
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

const CreateReelModal = ({ isOpen, onClose, onSubmit }) => {
  console.log('[CreateReelModal] Component rendering. isOpen:', isOpen);
  console.log('[CreateReelModal] API_BASE_URL:', API_BASE_URL);
  
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [category, setCategory] = useState('Entertainment');
  const [isPublic, setIsPublic] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('video/')) {
        alert('Please select a video file (MP4, MOV, AVI, etc.)');
        return;
      }

      // Check file size (max 100MB)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        alert('Video file is too large. Maximum size is 100MB.');
        return;
      }

      setVideoFile(file);
      setError(null);

      // Create preview
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
    }
  };

  const handleVideoDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('video/')) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleVideoMetadata = () => {
    if (videoRef.current) {
      setVideoDuration(Math.round(videoRef.current.duration));
    }
  };

  const handleRemoveVideo = () => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideoFile(null);
    setVideoPreview(null);
    setVideoDuration(0);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadVideoToServer = async () => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('caption', caption.trim());
      formData.append('hashtags', JSON.stringify(hashtags.split(' ').filter(tag => tag.startsWith('#'))));
      formData.append('category', category);
      formData.append('isPublic', isPublic);
      formData.append('duration', videoDuration);

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percentComplete);
        }
      });

      // Handle successful upload
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('[Upload] Success:', response);
            resolve(response.data);
          } catch (err) {
            reject(new Error('Failed to parse server response'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            reject(new Error(errorResponse.message || 'Upload failed'));
          } catch (err) {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      });

      // Handle network errors
      xhr.addEventListener('error', () => {
        reject(new Error('Network error. Please check your connection.'));
      });

      // Handle aborted uploads
      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      // Get auth token from localStorage
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('ZToken');
      
      // ✅ FIXED: Correct URL construction
      const uploadUrl = `${API_BASE_URL}/reels/upload`;
      console.log('[Upload] Uploading to:', uploadUrl);
      console.log('[Upload] Has token:', !!token);
      
      // Send request
      xhr.open('POST', uploadUrl);
      
      // Add authorization header if token exists
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      } else {
        console.warn('[Upload] No authentication token found!');
      }
      
      xhr.send(formData);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!videoFile) {
      alert('Please select a video file');
      return;
    }

    if (!caption.trim()) {
      alert('Please add a caption');
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('ZToken');
    if (!token) {
      setError('Please login to upload reels');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      console.log('[Upload] Starting video upload...');
      const uploadedReel = await uploadVideoToServer();
      
      console.log('[Upload] Video uploaded successfully:', uploadedReel);
      
      // Call parent's onSubmit with the uploaded reel data
      if (onSubmit) {
        onSubmit(uploadedReel);
      }
      
      // Close modal and reset form
      handleClose();
      
      alert('🎉 Reel uploaded successfully!');
    } catch (err) {
      console.error('[Upload] Error:', err);
      setError(err.message || 'Failed to upload video. Please try again.');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (uploading) {
      const confirmClose = window.confirm('Upload in progress. Are you sure you want to cancel?');
      if (!confirmClose) return;
    }
    
    handleRemoveVideo();
    setCaption('');
    setHashtags('');
    setCategory('Entertainment');
    setIsPublic(true);
    setUploadProgress(0);
    setError(null);
    setUploading(false);
    onClose();
  };

  if (!isOpen) {
    console.log('[CreateReelModal] Not rendering - isOpen is false');
    return null;
  }

  console.log('[CreateReelModal] Rendering modal UI');

  return (
    <div className="reel-modal-overlay" onClick={handleClose}>
      <div className="reel-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="reel-modal-header">
          <h2 className="reel-modal-title">🎥 Create Video Reel</h2>
          <button
            className="reel-modal-close"
            onClick={handleClose}
            disabled={uploading}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="reel-modal-form">
          {/* Error Message */}
          {error && (
            <div className="error-message" style={{
              backgroundColor: '#fee',
              color: '#c33',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #fcc',
              fontSize: '14px'
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Video Upload Area */}
          {!videoPreview ? (
            <div
              className="video-upload-area"
              onDrop={handleVideoDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="upload-icon">🎬</div>
              <h3 className="upload-title">Upload Video</h3>
              <p className="upload-subtitle">
                Drag and drop or click to browse
              </p>
              <p className="upload-info">
                MP4, MOV, AVI • Max 100MB • Max 60 seconds
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            <div className="video-preview-container">
              <video
                ref={videoRef}
                src={videoPreview}
                controls
                onLoadedMetadata={handleVideoMetadata}
                className="video-preview"
              />
              <button
                type="button"
                onClick={handleRemoveVideo}
                className="remove-video-btn"
                disabled={uploading}
              >
                ×
              </button>
              {videoDuration > 0 && (
                <div className="video-duration">
                  {Math.floor(videoDuration / 60)}:{(videoDuration % 60).toString().padStart(2, '0')}
                </div>
              )}
            </div>
          )}

          {/* Caption */}
          <div className="form-group">
            <label className="form-label">Caption *</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Write a catchy caption for your reel..."
              required
              maxLength={500}
              className="form-textarea"
              disabled={uploading}
            />
            <div className="char-count">
              {caption.length}/500
            </div>
          </div>

          {/* Hashtags */}
          <div className="form-group">
            <label className="form-label">Hashtags</label>
            <input
              type="text"
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
              placeholder="#fun #viral #trending"
              className="form-input"
              disabled={uploading}
            />
            <div className="form-hint">
              Separate hashtags with spaces
            </div>
          </div>

          {/* Category */}
          <div className="form-group">
            <label className="form-label">Category *</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
              className="form-select"
              disabled={uploading}
            >
              <option value="Entertainment">🎭 Entertainment</option>
              <option value="Sports">⚽ Sports</option>
              <option value="Music">🎵 Music</option>
              <option value="Comedy">😂 Comedy</option>
              <option value="Education">📚 Education</option>
              <option value="Food">🍔 Food</option>
              <option value="Travel">✈️ Travel</option>
              <option value="Fashion">👗 Fashion</option>
              <option value="Fitness">💪 Fitness</option>
              <option value="Art">🎨 Art</option>
              <option value="Technology">💻 Technology</option>
              <option value="Lifestyle">🌟 Lifestyle</option>
              <option value="Other">📌 Other</option>
            </select>
          </div>

          {/* Privacy */}
          <div className="form-group">
            <label className="form-label">Privacy</label>
            <div className="privacy-options">
              <label className={`privacy-option ${isPublic ? 'active' : ''}`}>
                <input
                  type="radio"
                  checked={isPublic}
                  onChange={() => setIsPublic(true)}
                  disabled={uploading}
                />
                <div>
                  <div className="privacy-title">🌍 Public</div>
                  <div className="privacy-subtitle">Everyone can see</div>
                </div>
              </label>
              <label className={`privacy-option ${!isPublic ? 'active' : ''}`}>
                <input
                  type="radio"
                  checked={!isPublic}
                  onChange={() => setIsPublic(false)}
                  disabled={uploading}
                />
                <div>
                  <div className="privacy-title">🔒 Friends</div>
                  <div className="privacy-subtitle">Friends only</div>
                </div>
              </label>
            </div>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="upload-progress">
              <div className="progress-header">
                <span>Uploading video...</span>
                <span className="progress-percent">{uploadProgress}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              className="btn-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!videoFile || !caption.trim() || uploading}
              className="btn-submit"
            >
              {uploading ? `Uploading... ${uploadProgress}%` : 'Post Reel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateReelModal;
